import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  TrendingDown,
  Equal,
  GitCompare,
  ArrowLeftRight,
  Percent,
  Search,
  X
} from 'lucide-react';
import {
  AlertType,
  TriggerConfig,
  ALERT_TYPE_INFO,
  getAlertTypesByCategory,
  ProductForAlert
} from '../../types/alerts';

interface AlertTypeSelectorProps {
  selectedType: AlertType | null;
  triggerConfig: TriggerConfig;
  onTypeChange: (type: AlertType) => void;
  onConfigChange: (config: TriggerConfig) => void;
  onSearchProduct?: (term: string) => Promise<ProductForAlert[]>;
  currentPrice?: number;
}

const TYPE_ICONS: Record<AlertType, React.ReactNode> = {
  price_greater_than: <TrendingUp className="h-4 w-4" />,
  price_less_than: <TrendingDown className="h-4 w-4" />,
  price_equals: <Equal className="h-4 w-4" />,
  price_vs_product: <GitCompare className="h-4 w-4" />,
  distance_value_abs: <ArrowLeftRight className="h-4 w-4" />,
  distance_value_pct: <Percent className="h-4 w-4" />,
  distance_product_abs: <ArrowLeftRight className="h-4 w-4" />,
  distance_product_pct: <Percent className="h-4 w-4" />
};

const CATEGORY_LABELS: Record<string, string> = {
  price: 'alerts.categories.price',
  comparison: 'alerts.categories.comparison',
  availability: 'alerts.categories.availability'
};

export const AlertTypeSelector: React.FC<AlertTypeSelectorProps> = ({
  selectedType,
  triggerConfig,
  onTypeChange,
  onConfigChange,
  onSearchProduct,
  currentPrice
}) => {
  const { t } = useTranslation();
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductForAlert[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const typesByCategory = getAlertTypesByCategory();

  // Prefill reference/target with current price if available and empty
  useEffect(() => {
    if (!selectedType || currentPrice === undefined) return;
    if (triggerConfig.targetValue === undefined) {
      onConfigChange({
        ...triggerConfig,
        targetValue: currentPrice,
        referencePrice: currentPrice
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, currentPrice]);

  // Búsqueda de productos
  useEffect(() => {
    if (!productSearchTerm || productSearchTerm.length < 2 || !onSearchProduct) {
      setProductSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await onSearchProduct(productSearchTerm);
        setProductSearchResults(results);
        setShowProductDropdown(true);
      } catch (err) {
        console.error('Error searching products:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [productSearchTerm, onSearchProduct]);

  const handleSelectReferenceProduct = (product: ProductForAlert) => {
    onConfigChange({
      ...triggerConfig,
      referenceProductId: product.id_catalogo,
      referenceProductTitle: product.title,
      referenceProductSitio: product.sitio
    });
    setProductSearchTerm('');
    setShowProductDropdown(false);
    setProductSearchResults([]);
  };

  const handleClearReferenceProduct = () => {
    onConfigChange({
      ...triggerConfig,
      referenceProductId: undefined,
      referenceProductTitle: undefined,
      referenceProductSitio: undefined
    });
  };

  const renderInlineConfig = (typeInfo: typeof ALERT_TYPE_INFO[number]) => {
    if (selectedType !== typeInfo.type) return null;

    return (
      <div className="mt-3 space-y-3">
        {/* Campo de valor para alertas de precio */}
        {typeInfo.requiresValue && !typeInfo.requiresProduct && (() => {
          const mode = triggerConfig.valueMode || 'absolute';
          const basePrice = triggerConfig.referencePrice ?? currentPrice ?? 0;
          const percentVal = triggerConfig.percentValue ?? 0;

          const handleModeChange = (newMode: 'absolute' | 'percent') => {
            const nextConfig: TriggerConfig = {
              ...triggerConfig,
              valueMode: newMode,
              referencePrice: basePrice
            };
            if (newMode === 'absolute') {
              nextConfig.targetValue = triggerConfig.targetValue ?? basePrice;
            } else {
              nextConfig.percentValue = percentVal;
              const factor = typeInfo.type === 'price_less_than'
                ? 1 - (percentVal / 100)
                : 1 + (percentVal / 100);
              nextConfig.targetValue = Math.max(0, basePrice * factor);
            }
            onConfigChange(nextConfig);
          };

          const handlePercentChange = (val?: number) => {
            const pct = val ?? 0;
            const factor = typeInfo.type === 'price_less_than'
              ? 1 - (pct / 100)
              : 1 + (pct / 100);
            onConfigChange({
              ...triggerConfig,
              valueMode: 'percent',
              percentValue: pct,
              referencePrice: basePrice,
              targetValue: Math.max(0, basePrice * factor)
            });
          };

          const handleBasePriceChange = (val?: number) => {
            const base = val ?? 0;
            if (mode === 'percent') {
              const pct = triggerConfig.percentValue ?? 0;
              const factor = typeInfo.type === 'price_less_than'
                ? 1 - (pct / 100)
                : 1 + (pct / 100);
              onConfigChange({
                ...triggerConfig,
                referencePrice: base,
                valueMode: 'percent',
                targetValue: Math.max(0, base * factor)
              });
            } else {
              onConfigChange({
                ...triggerConfig,
                referencePrice: base,
                targetValue: base,
                valueMode: 'absolute'
              });
            }
          };

          return (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                  <button
                    onClick={() => handleModeChange('absolute')}
                    className={`px-3 py-1.5 text-xs ${
                      mode === 'absolute' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'
                    }`}
                  >
                    $
                  </button>
                  <button
                    onClick={() => handleModeChange('percent')}
                    className={`px-3 py-1.5 text-xs ${
                      mode === 'percent' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'
                    }`}
                  >
                    %
                  </button>
                </div>

                {mode === 'absolute' ? (
                  <input
                    type="number"
                    value={triggerConfig.targetValue ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      onConfigChange({
                        ...triggerConfig,
                        valueMode: 'absolute',
                        targetValue: value,
                        referencePrice: basePrice
                      });
                    }}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="1000"
                    min="0"
                    step="1"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={percentVal || ''}
                      onChange={(e) => handlePercentChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="10"
                      min="0"
                      step="0.1"
                    />
                    <span className="text-xs text-gray-500">%</span>
                    <span className="text-xs text-gray-500">de</span>
                    <span className="text-xs text-gray-500">$</span>
                    <input
                      type="number"
                      value={basePrice || ''}
                      onChange={(e) => handleBasePriceChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="100"
                      min="0"
                      step="0.5"
                    />
                  </div>
                )}
              </div>

              {mode === 'percent' && triggerConfig.targetValue !== undefined && (
                <p className="text-xs text-gray-500">
                  {`Emite alerta si es $${triggerConfig.targetValue.toFixed(2)}`}
                </p>
              )}
            </div>
          );
        })()}

        {/* Selector de operador para distancia */}
        {typeInfo.requiresDistance && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              {t('alerts.distance_operator')}
            </label>
            <select
              value={triggerConfig.distanceOperator || 'greater'}
              onChange={(e) => onConfigChange({
                ...triggerConfig,
                distanceOperator: e.target.value as 'greater' | 'less' | 'equal'
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="greater">{t('alerts.operators.greater')}</option>
              <option value="less">{t('alerts.operators.less')}</option>
              <option value="equal">{t('alerts.operators.equal')}</option>
            </select>
          </div>
        )}

        {/* Buscador de producto de referencia */}
        {typeInfo.requiresProduct && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              {t('alerts.reference_product')}
            </label>

            {triggerConfig.referenceProductId ? (
              <div className="flex items-center justify-between p-3 bg-white border border-gray-300 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {triggerConfig.referenceProductTitle}
                  </p>
                  <p className="text-xs text-gray-500">
                    {triggerConfig.referenceProductSitio} - {triggerConfig.referenceProductId}
                  </p>
                </div>
                <button
                  onClick={handleClearReferenceProduct}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    onFocus={() => productSearchResults.length > 0 && setShowProductDropdown(true)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('alerts.search_product_placeholder')}
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                  )}
                </div>

                {showProductDropdown && productSearchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {productSearchResults.map((product) => (
                      <button
                        key={product.id_catalogo}
                        onClick={() => handleSelectReferenceProduct(product)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                      >
                        {product.imagen && (
                          <img
                            src={product.imagen}
                            alt=""
                            className="h-10 w-10 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {product.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {product.sitio}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Operador de comparación para price_vs_product */}
            {typeInfo.type === 'price_vs_product' && (
              <div className="mt-3">
                <label className="block text-sm text-gray-600 mb-1">
                  {t('alerts.comparison_operator')}
                </label>
                <select
                  value={triggerConfig.comparisonOperator || 'greater'}
                  onChange={(e) => onConfigChange({
                    ...triggerConfig,
                    comparisonOperator: e.target.value as 'greater' | 'less' | 'equal'
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="greater">{t('alerts.comparison.greater')}</option>
                  <option value="less">{t('alerts.comparison.less')}</option>
                  <option value="equal">{t('alerts.comparison.equal')}</option>
                </select>
              </div>
            )}

            {/* Distancia para distance_product_* */}
            {(typeInfo.type === 'distance_product_abs' || typeInfo.type === 'distance_product_pct') && (
              <div className="mt-3">
                <label className="block text-sm text-gray-600 mb-1">
                  {typeInfo.type === 'distance_product_pct' ? t('alerts.distance_percent') : t('alerts.distance_value')}
                </label>
                <div className="flex items-center">
                  {typeInfo.type === 'distance_product_abs' && (
                    <span className="text-gray-500 mr-2">$</span>
                  )}
                  <input
                    type="number"
                    value={triggerConfig.distanceValue || ''}
                    onChange={(e) => onConfigChange({
                      ...triggerConfig,
                      distanceValue: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={typeInfo.type === 'distance_product_pct' ? '10' : '1000'}
                    min="0"
                  />
                  {typeInfo.type === 'distance_product_pct' && (
                    <span className="text-gray-500 ml-2">%</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mensaje para tipos sin configuración adicional */}
        {!typeInfo.requiresValue && !typeInfo.requiresProduct && (
          <p className="text-sm text-gray-500 italic">
            {t('alerts.no_config_needed')}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-700">{t('alerts.select_type')}</h3>

      {Object.entries(typesByCategory).map(([category, types]) => (
        <div key={category} className="space-y-2">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t(CATEGORY_LABELS[category])}
          </h4>

          <div className="space-y-1">
            {types.map((typeInfo) => (
              <div key={typeInfo.type} className="space-y-1">
                {(() => {
                  const isSelected = selectedType === typeInfo.type;
                  const isConfigured =
                    isSelected &&
                    (
                      (typeInfo.requiresValue && triggerConfig.targetValue !== undefined) ||
                      (typeInfo.requiresProduct && !!triggerConfig.referenceProductId) ||
                      (!typeInfo.requiresValue && !typeInfo.requiresProduct)
                    );
                  const activeClass = isConfigured
                    ? 'bg-green-50 border-2 border-green-500 text-green-700'
                    : 'bg-blue-50 border-2 border-blue-500 text-blue-700';
                  return (
                    <button
                      onClick={() => onTypeChange(typeInfo.type)}
                      className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                        isSelected
                          ? activeClass
                          : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className={`mr-3 ${isSelected ? (isConfigured ? 'text-green-600' : 'text-blue-500') : 'text-gray-400'}`}>
                        {TYPE_ICONS[typeInfo.type]}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{t(typeInfo.labelKey)}</p>
                        <p className="text-xs text-gray-500">{t(typeInfo.descriptionKey)}</p>
                      </div>
                    </button>
                  );
                })()}

                {/* Configuración inline debajo de la opción seleccionada */}
                {renderInlineConfig(typeInfo)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
